import { rewriteKey } from "./grade-rewrite.js";

/**
 * Marking a reply a reader wrote to a turn of a staged reading passage
 * (CB-77), in the browser and in the handler alike.
 *
 * A written reply is checked against the replies a person approved, the way
 * a Transform item's rewrite is (`./grade-rewrite.ts`), punctuation and
 * spaces dropped. There are four outcomes, the distinctions the grammar
 * feedback draws (#1308):
 *
 * - `right`: one of the checked replies.
 * - `other`: one of them in the other writing, which passes as 还行 does.
 * - `known`: a wrong reply a learner is likely to write, with a reviewed note
 *   on what is wrong with it.
 * - `unchecked`: anything else. Nobody has checked it, so the page calls it
 *   neither right nor wrong, and no model is asked while the reader waits.
 *
 * **A reply that misses gets one more try** (CB-77). `replyMoves` says what a
 * reply does to the question it answers: the first `known` or `unchecked`
 * reply shows the hint and asks again, and the second ends the question. A
 * right reply ends it at either try.
 */

/** What a written reply is marked against, in both writings. */
export interface YueduReplyKeys {
  /** Every checked right reply, in 简体, the passage's own turn first. */
  readonly accepted: readonly string[];

  /** The same replies in 繁體, in the same order. */
  readonly other: readonly string[];

  /** Wrong replies a learner is likely to write. */
  readonly known: readonly YueduKnownKey[];
}

/** A known wrong reply in both writings, with what is wrong with it. */
export interface YueduKnownKey {
  readonly hanzi: string;
  readonly other: string;
  readonly why: string;
}

/** How a written reply is marked. */
export type YueduReplyOutcome = "right" | "other" | "known" | "unchecked";

/** A reply marked, and which known reply it is where it is one. */
export interface YueduReplyMark {
  readonly outcome: YueduReplyOutcome;
  readonly known?: number;
}

/** How many tries a written reply is given. */
export const yueduReplyTries = 2;

/**
 * Mark a reply.
 * @param typed - What the reader wrote.
 * @param keys - The checked replies and the known wrong ones.
 * @returns The outcome, and the known reply matched.
 */
export function markReply(typed: string, keys: YueduReplyKeys): YueduReplyMark {
  const key = rewriteKey(typed);
  const among = (sentences: readonly string[]): boolean =>
    sentences.some((sentence) => rewriteKey(sentence) === key);

  if (key === "") {
    return { outcome: "unchecked" };
  }

  if (among(keys.accepted)) {
    return { outcome: "right" };
  }

  if (among(keys.other)) {
    return { outcome: "other" };
  }

  const known = keys.known.findIndex(({ hanzi, other }) =>
    among([hanzi, other]),
  );

  return known === -1 ? { outcome: "unchecked" } : { outcome: "known", known };
}

/** Whether an outcome passes. */
export function replyPasses(outcome: YueduReplyOutcome): boolean {
  return outcome === "right" || outcome === "other";
}

/** What a reply does to its question. */
export interface YueduReplyMove {
  /** Whether the question is done with, and leaves the queue. */
  readonly done: boolean;

  /** Where it is done with, whether it counts as a miss. */
  readonly missed: boolean;

  /** Where it is done with, whether it counts as neither right nor wrong. */
  readonly unchecked: boolean;
}

/**
 * What a reply does to its question, given the tries made before it.
 * @param outcome - How the reply was marked.
 * @param before - The tries made at the question before this one.
 * @returns Whether the question is done, and how it is counted.
 */
export function replyMoves(
  outcome: YueduReplyOutcome,
  before: number,
): YueduReplyMove {
  if (replyPasses(outcome)) {
    return { done: true, missed: false, unchecked: false };
  }

  if (before + 1 < yueduReplyTries) {
    return { done: false, missed: false, unchecked: false };
  }

  return {
    done: true,
    missed: outcome === "known",
    unchecked: outcome === "unchecked",
  };
}

/** Whether a value is a list of strings. */
function texts(list: unknown): boolean {
  return Array.isArray(list) && list.every((each) => typeof each === "string");
}

/** Whether a value read back is a set of reply keys. */
export function isReplyKeys(value: unknown): value is YueduReplyKeys {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const { accepted, other, known } = value as Record<string, unknown>;

  return (
    texts(accepted) &&
    (accepted as readonly string[]).length > 0 &&
    texts(other) &&
    Array.isArray(known) &&
    known.every((each: unknown) => {
      const fields = (each ?? {}) as Record<string, unknown>;

      return (
        typeof fields["hanzi"] === "string" &&
        typeof fields["other"] === "string" &&
        typeof fields["why"] === "string"
      );
    })
  );
}
